import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, Check, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUCCESS_MSG = 'Inscription enregistrée. Si vous n’êtes pas déjà abonné(e), un email de bienvenue vous a été envoyé.';
const ERROR_BY_STATUS = { 429: 'Trop de tentatives. Merci de réessayer plus tard.', 422: 'Adresse email invalide.' };
const errorFor = (status) => ERROR_BY_STATUS[status] || 'Inscription impossible pour le moment. Réessayez plus tard.';

const useNewsletterSubmit = ({ email, source, website, consent, onSuccess }) => {
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setStatus({ state: 'error', msg: 'Merci de cocher la case de consentement.' }); return; }
    setStatus({ state: 'loading', msg: '' });
    try {
      await axios.post(`${API}/newsletter`, { email, source, website });
      setStatus({ state: 'success', msg: SUCCESS_MSG });
      onSuccess();
    } catch (err) {
      setStatus({ state: 'error', msg: errorFor(err?.response?.status) });
    }
  };
  return { status, submit };
};

const NewsletterForm = ({ source = 'site' }) => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [consent, setConsent] = useState(false);
  const reset = () => { setEmail(''); setConsent(false); };
  const { status, submit: onSubmit } = useNewsletterSubmit({ email, source, website, consent, onSuccess: reset });

  return (
    <section className="bg-[#010c23] text-white" data-testid="newsletter-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-14 bg-[#ff7300]" />
            <span className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Newsletter patrimoniale</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-light leading-tight">Recevez nos analyses, <span className="italic text-[#ff7300]">avant tout le monde</span></h2>
          <p className="mt-5 text-white/70 max-w-xl leading-relaxed">Fiscalité, assurance-vie, retraite, transmission, marchés : des analyses claires et accessibles pour mieux comprendre les enjeux patrimoniaux et éclairer vos décisions.</p>
        </div>
        <form onSubmit={onSubmit} className="lg:col-span-6 bg-white/5 border border-white/10 p-8 backdrop-blur" data-testid="newsletter-form">
          <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
          <label className="text-xs uppercase tracking-widest text-white/60">Votre adresse email</label>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@exemple.fr" className="flex-1 bg-transparent border-b border-white/30 focus:border-[#ff7300] outline-none py-3 text-white placeholder:text-white/30" data-testid="newsletter-email-input" />
            <button type="submit" disabled={status.state === 'loading'} className="btn-primary disabled:opacity-70" data-testid="newsletter-submit-button">
              {status.state === 'loading' ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <>S’inscrire <Send size={16} /></>}
            </button>
          </div>
          <div className="flex items-start gap-3 mt-5">
            <input type="checkbox" id="nl-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#ff7300]" data-testid="newsletter-consent-checkbox" />
            <label htmlFor="nl-consent" className="text-xs text-white/60 leading-relaxed">J’accepte de recevoir la newsletter d’Ometis Patrimoine. Je peux me désinscrire à tout moment. <Link to="/reglementaire#confidentialite" className="text-[#ff7300] hover:underline">Politique de confidentialité</Link>.</label>
          </div>
          {status.state === 'success' && <p className="mt-4 flex items-start gap-2 text-sm text-white bg-[#ff7300]/20 border border-[#ff7300]/40 p-3" data-testid="newsletter-success"><Check size={16} className="mt-0.5 text-[#ff7300]" /> {status.msg}</p>}
          {status.state === 'error' && <p className="mt-4 flex items-start gap-2 text-sm text-red-200 bg-red-900/30 border border-red-400/40 p-3" data-testid="newsletter-error"><AlertCircle size={16} className="mt-0.5" /> {status.msg}</p>}
        </form>
      </div>
    </section>
  );
};

export default NewsletterForm;
